import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import Cookies from 'js-cookie'
import { ReactNode, useEffect } from 'react'
import CookieConsent from 'react-cookie-consent'
import { default as ReactGA, default as ReactGA4 } from 'react-ga4'
import { useTranslation } from 'react-i18next'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import images from '../../assets/img/images'
import { useAuthCheck } from '../../hooks/useAuthCheck'
import FaqPage from '../../pages/FaqPage'
import AdminCarousel from '../../pages/admin/AdminCarousel'
import AdminDashboard from '../../pages/admin/AdminDashboard'
import Login from '../../pages/admin/Login'
import AdminBadnetTournamentForm from '../../pages/admin/badnet/AdminBadnetTournamentForm'
import AdminBadnetTournaments from '../../pages/admin/badnet/AdminBadnetTournaments'
import AdminChatBot from '../../pages/admin/chatBot/AdminChatBot'
import AdminChatBotForm from '../../pages/admin/chatBot/AdminChatBotForm'
import AdminCoach from '../../pages/admin/coach/AdminCoach'
import AdminCoachForm from '../../pages/admin/coach/AdminCoachForm'
import AdminDocumentation from '../../pages/admin/documentation/AdminDocumentation'
import AdminDocumentationForm from '../../pages/admin/documentation/AdminDocumentationForm'
import AdminToDocumentMyself from '../../pages/admin/documentation/AdminToDocumentMyself'
import AdminFaq from '../../pages/admin/faq/AdminFaq'
import AdminFaqForm from '../../pages/admin/faq/AdminFaqForm'
import AdminNews from '../../pages/admin/news/AdminNews'
import AdminNewsForm from '../../pages/admin/news/AdminNewsForm'
import AdminPartners from '../../pages/admin/partners/AdminPartners'
import AdminPartnersForm from '../../pages/admin/partners/AdminPartnersForm'
import AdminPlannings from '../../pages/admin/plannings/AdminPlannings'
import AdminPlanningsForm from '../../pages/admin/plannings/AdminPlanningsForm'
import AdminPriceForm from '../../pages/admin/prices/AdminPriceForm'
import AdminPrices from '../../pages/admin/prices/AdminPrices'
import AdminSession from '../../pages/admin/session/AdminSessions'
import AdminSessionsForm from '../../pages/admin/session/AdminSessionsForm'
import AdminUser from '../../pages/admin/users/AdminUsers'
import AdminUserForm from '../../pages/admin/users/AdminUsersForm'
import CoachDashboard from '../../pages/coach/CoachDashboard'
import CoachEditPassword from '../../pages/coach/CoachEditPassword'
import CoachExercises from '../../pages/coach/CoachExercises'
import CoachTrainingSession from '../../pages/coach/CoachTrainingSession'
import CoachTrainingSessionGroups from '../../pages/coach/CoachTrainingSessionGroups'
import CoachMyMembers from '../../pages/coach/my-members/CoachMyMembers'
import CoachMyMembersAddMemberForm from '../../pages/coach/my-members/CoachMyMembersAddMemberForm'
import CoachMyMembersSingleMemberSkillsForm from '../../pages/coach/my-members/CoachMyMembersSingleMemberSkillsForm'
import CoachMyMembersSingleMemberView from '../../pages/coach/my-members/CoachMyMembersSingleMemberView'
import CoachParentSkills from '../../pages/coach/parent-skills/CoachParentSkills'
import CoachSkills from '../../pages/coach/skills/CoachSkills'
import Page404 from '../../pages/global/404'
import BadnetTournaments from '../../pages/global/BadnetTournaments'
import Contact from '../../pages/global/Contact'
import Home from '../../pages/global/Home'
import PlanningPage from '../../pages/global/PlanningPage'
import Privacy from '../../pages/global/Privacy'
import MemberDashboard from '../../pages/member/MemberDashboard'
import MemberDocumentation from '../../pages/member/MemberDocumentation'
import MemberEditPassword from '../../pages/member/MemberEditPassword'
import MemberMyProgression from '../../pages/member/MemberMyProgression'
import MemberSatisfactionSurvey from '../../pages/member/MemberSatisfactionSurvey'
import UserInterface from './user-interface/UserInterface'

const CookiesComponent = (props: ButtonCookieProps) => <CookiesButton {...props}>{props.text}</CookiesButton>

const Router = () => {
    const { isAdmin, isMember, isCoach, adminData, memberData, coachData, isLoading } = useAuthCheck()

    let lng =
        window.location.pathname
            .split('/')
            .filter((x, i) => i === 1)
            .join('/') || 'fr'

    if (
        !window.location.pathname
            .split('/')
            .filter((x, i) => i === 1)
            .join('/') &&
        !lng
    ) {
        lng = 'fr'
        window.location.replace('/fr')
    }

    const checkAuthentification = (element: ReactNode, data: any) => {
        if (isLoading) {
            return (
                <LoadingContainer>
                    <LoadingSpinner />
                </LoadingContainer>
            )
        }
        if (data) return element
        return <Navigate to={`/${lng}/login`} />
    }

    const { t } = useTranslation('translation')

    useEffect(() => {
        if (Cookies.get('all_cookies') === 'true') {
            ReactGA4.initialize('G-FVZ622CSL8', {
                gaOptions: { cookieDomain: 'auto', cookieExpires: 86400 },
            })

            ReactGA.send({ hitType: 'pageview', page: window.location.pathname })
        }
    })

    return (
        <BrowserRouter>
            {!Cookies.get('all_cookies') && (
                <CookieConsent
                    enableDeclineButton
                    location="bottom"
                    ButtonComponent={CookiesComponent}
                    customButtonProps={{
                        style: { backgroundColor: 'green' },
                        text: 'Tout accepter',
                        'data-testid': 'accept-cookies',
                    }}
                    customDeclineButtonProps={{
                        style: { backgroundColor: 'red' },
                        text: 'Tout refuser',
                        'data-testid': 'decline-cookies',
                    }}
                    cookieName="all_cookies"
                    style={{ background: '#2B373B' }}
                    expires={1}
                >
                    <CookieImg src={images.cookie} alt="cookie image" /> {t('cookies')}
                </CookieConsent>
            )}
            <UserInterface>
                <Routes>
                    <Route path="/:lng" element={<Home />} />
                    <Route path="/:lng/privacy" element={<Privacy />} />
                    <Route path="/:lng/contact" element={<Contact />} />
                    <Route path="/:lng/faq" element={<FaqPage />} />
                    <Route path="/:lng/tournois-badnet" element={<BadnetTournaments />} />
                    <Route path="/:lng/horaires-et-tarifs" element={<PlanningPage />} />
                    <Route path="/:lng/bva-admin" element={checkAuthentification(<AdminDashboard />, adminData)} />
                    <Route
                        path="/:lng/bva-admin/utilisateurs"
                        element={checkAuthentification(<AdminUser />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/utilisateurs/creer"
                        element={checkAuthentification(<AdminUserForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/utilisateurs/:id"
                        element={checkAuthentification(<AdminUserForm />, adminData)}
                    />
                    <Route path="/:lng/bva-admin/articles" element={checkAuthentification(<AdminNews />, adminData)} />
                    <Route
                        path="/:lng/bva-admin/articles/creer"
                        element={checkAuthentification(<AdminNewsForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/articles/:id"
                        element={checkAuthentification(<AdminNewsForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/partenaires"
                        element={checkAuthentification(<AdminPartners />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/partenaires/creer"
                        element={checkAuthentification(<AdminPartnersForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/partenaires/:id"
                        element={checkAuthentification(<AdminPartnersForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/tournois-badnet"
                        element={checkAuthentification(<AdminBadnetTournaments />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/badnet/:id"
                        element={checkAuthentification(<AdminBadnetTournamentForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/plannings"
                        element={checkAuthentification(<AdminPlannings />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/plannings/creer"
                        element={checkAuthentification(<AdminPlanningsForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/plannings/:id"
                        element={checkAuthentification(<AdminPlanningsForm />, adminData)}
                    />
                    <Route path="/:lng/bva-admin/tarifs" element={checkAuthentification(<AdminPrices />, adminData)} />
                    <Route
                        path="/:lng/bva-admin/tarifs/:id"
                        element={checkAuthentification(<AdminPriceForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/tarifs/creer"
                        element={checkAuthentification(<AdminPriceForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/documentation"
                        element={checkAuthentification(<AdminDocumentation />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/documentation/:id"
                        element={checkAuthentification(<AdminDocumentationForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/documentation/creer"
                        element={checkAuthentification(<AdminDocumentationForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/me-documenter"
                        element={checkAuthentification(<AdminToDocumentMyself />, adminData)}
                    />
                    <Route path="/:lng/bva-admin/coachs" element={checkAuthentification(<AdminCoach />, adminData)} />
                    <Route
                        path="/:lng/bva-admin/coachs/:id"
                        element={checkAuthentification(<AdminCoachForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/coachs/creer"
                        element={checkAuthentification(<AdminCoachForm />, adminData)}
                    />
                    <Route path="/:lng/bva-admin/faq" element={checkAuthentification(<AdminFaq />, adminData)} />
                    <Route
                        path="/:lng/bva-admin/faq/creer"
                        element={checkAuthentification(<AdminFaqForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/faq/:id"
                        element={checkAuthentification(<AdminFaqForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/sessions"
                        element={checkAuthentification(<AdminSession />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/sessions/:id"
                        element={checkAuthentification(<AdminSessionsForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/sessions/creer"
                        element={checkAuthentification(<AdminSessionsForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/bva-bot"
                        element={checkAuthentification(<AdminChatBot />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/bva-bot/creer"
                        element={checkAuthentification(<AdminChatBotForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/bva-bot/:id"
                        element={checkAuthentification(<AdminChatBotForm />, adminData)}
                    />
                    <Route
                        path="/:lng/bva-admin/carousel"
                        element={checkAuthentification(<AdminCarousel />, adminData)}
                    />
                    <Route path="/:lng/login" element={<Login />} />
                    <Route
                        path="/:lng/bva-admin/*"
                        element={adminData ? <Navigate to={`/${lng}/bva-admin`} /> : <Login />}
                    />
                    <Route path="/:lng/bva-membre" element={checkAuthentification(<MemberDashboard />, memberData)} />
                    <Route
                        path="/:lng/bva-membre/ma-progression"
                        element={checkAuthentification(<MemberMyProgression />, memberData)}
                    />
                    <Route
                        path="/:lng/bva-membre/documentation"
                        element={checkAuthentification(<MemberDocumentation />, memberData)}
                    />
                    <Route
                        path="/:lng/bva-membre/modifier-mon-mot-de-passe"
                        element={checkAuthentification(<MemberEditPassword />, memberData)}
                    />
                    <Route
                        path="/:lng/bva-membre/questionnaire-satisfaction"
                        element={checkAuthentification(<MemberSatisfactionSurvey />, memberData)}
                    />
                    <Route
                        path="/:lng/bva-membre/*"
                        element={memberData ? <Navigate to={`/${lng}/bva-membre`} /> : <Login />}
                    />
                    <Route path="/:lng/bva-coach" element={checkAuthentification(<CoachDashboard />, coachData)} />
                    <Route
                        path="/:lng/bva-coach/competences"
                        element={checkAuthentification(<CoachSkills />, coachData)}
                    />
                    <Route
                        path="/:lng/bva-coach/categories-competences"
                        element={checkAuthentification(<CoachParentSkills />, coachData)}
                    />
                    <Route
                        path="/:lng/bva-coach/exercises"
                        element={checkAuthentification(<CoachExercises />, coachData)}
                    />
                    <Route
                        path="/:lng/bva-coach/seances"
                        element={checkAuthentification(<CoachTrainingSession />, coachData)}
                    />
                    <Route
                        path="/:lng/bva-coach/groupes-seances"
                        element={checkAuthentification(<CoachTrainingSessionGroups />, coachData)}
                    />
                    <Route
                        path="/:lng/bva-coach/mes-membres"
                        element={checkAuthentification(<CoachMyMembers />, coachData)}
                    />
                    <Route
                        path="/:lng/bva-coach/mes-membres/selectionner-des-membres"
                        element={checkAuthentification(<CoachMyMembersAddMemberForm />, coachData)}
                    />
                    <Route
                        path="/:lng/bva-coach/mes-membres/:memberId"
                        element={checkAuthentification(<CoachMyMembersSingleMemberView />, coachData)}
                    />
                    <Route
                        path="/:lng/bva-coach/mes-membres/:memberId/:skillId"
                        element={checkAuthentification(<CoachMyMembersSingleMemberSkillsForm />, coachData)}
                    />
                    <Route
                        path="/:lng/bva-coach/modifier-mon-mot-de-passe"
                        element={checkAuthentification(<CoachEditPassword />, coachData)}
                    />
                    <Route
                        path="/:lng/bva-coach/*"
                        element={coachData ? <Navigate to={`/${lng}/bva-coach`} /> : <Login />}
                    />
                    <Route path="/:lng/*" element={<Page404 />}></Route>
                </Routes>
            </UserInterface>
            {/* <ChatBot /> */}
        </BrowserRouter>
    )
}

const cookieAnimation = keyframes`
  100% {
    transform: rotate(360deg);
  }
`

const CookieImg = styled.img`
    width: 60px;
    animation: ${cookieAnimation} 10s linear infinite;
`

const CookiesButton = styled.button`
    border: none;
    webkit-appearance: none;
    margin-right: 8px;
    margin-bottom: 8px;
    margin-left: 8px;
    padding: 8px 16px;
    border-radius: 32px;
    color: white;

    &:active {
        transform: scale(0.95);
    }
`

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background: var(--main-bg-color);
`

const LoadingSpinner = styled.div`
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid var(--primary-color, #b40000);
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`

type ButtonCookieProps = {
    text: string
    style: {
        backgroundColor: string
    }
}

export default Router
